import { type UseModalState } from "@clipboard-health/ui-components";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { getValueInUsd } from "@src/appV2/lib/Money/utils/currency";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { max, parseISO } from "date-fns";
import { useEffect } from "react";

import { calculateMinutesElapsed } from "../../ArrivalTime/calculateMinutesElapsed";
import {
  SHIFT_DISCOVERY_SHIFT_AUCTION_REVIEW_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { getScheduledShiftDurationInMinutes } from "../../utils/getScheduledShiftDuration";
import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { type ModalShiftData } from "../types";
import { SHIFT_AUCTION_BID_PAY_PARAMETER_NAME, SHIFT_AUCTION_LABEL } from "./constants";
import { PlaceBidBottomSheet } from "./PlaceBidBottomSheet";

export interface PlaceBidBottomSheetContainerProps {
  modalState: UseModalState;
  shift: ModalShiftData;
  inputBidPay?: number;
}

export function PlaceBidBottomSheetContainer(props: PlaceBidBottomSheetContainerProps) {
  const { modalState, shift, inputBidPay } = props;

  const {
    id: shiftId,
    workplace,
    shiftAuction,
    shiftAuctionBid,
    attributes: { start, end, isInstantPay },
  } = shift;

  const {
    requiresLunchBreak,
    name: workplaceName,
    location: { timezone },
  } = workplace.attributes;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: workplace.id,
  });

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_AUCTION_PLACE_BID_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const { navigateToModal } = useShiftModalsDataContext();

  const handlePlaceBid = async (pay: number) => {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_AUCTION_REVIEW_BID_MODAL_PATH, {
      pathParams: { shiftId },
      searchParams: { [SHIFT_AUCTION_BID_PAY_PARAMETER_NAME]: pay.toString() },
    });
  };

  const { showInfoToast } = useToast();

  const handleBack = () => {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
      pathParams: { shiftId },
      clearExistingSearchParams: true,
    });
  };

  // This may happen if the shift is no longer available for bidding
  useEffect(() => {
    if (!shiftAuction) {
      showInfoToast(`No ${SHIFT_AUCTION_LABEL} auction, returning to shift details`);
      logEvent(APP_V2_USER_EVENTS.SHIFT_AUCTION_PLACE_BID_NO_AUCTION, { shiftId });
      navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: true,
      });
    }
  }, [shiftAuction, shiftId, navigateToModal, showInfoToast]);

  if (!shiftAuction) {
    return null;
  }

  const startDate = parseISO(start);
  const endDate = parseISO(end);

  const scheduledShiftDurationInHours = getScheduledShiftDurationInMinutes({ start, end }) / 60;
  const minutesElapsed = calculateMinutesElapsed(
    startDate,
    max([shiftAuction.attributes.endTime, new Date()])
  );
  const remainingWorkDurationInHours = getShiftWorkDurationInHours(
    { start, end },
    Boolean(requiresLunchBreak),
    mandatoryBreakDurationInMinutes,
    minutesElapsed
  );

  const {
    averageWinningBid,
    averageTotalBidders,
    maximumPay,
    minimumPay,
    endTime: auctionEndTime,
  } = shiftAuction.attributes;

  const minimumAuctionPay = getValueInUsd(minimumPay.amountInMinorUnits);
  const maximumAuctionPay = getValueInUsd(maximumPay.amountInMinorUnits);

  let initialSliderValue = minimumAuctionPay;

  const averageWinningBidInUsd = averageWinningBid
    ? getValueInUsd(averageWinningBid.amountInMinorUnits)
    : undefined;

  const auctionBidAmount = shiftAuctionBid
    ? getValueInUsd(shiftAuctionBid.attributes.bidAmount.amountInMinorUnits)
    : undefined;

  if (auctionBidAmount) {
    initialSliderValue = auctionBidAmount;
  } else if (inputBidPay && inputBidPay > initialSliderValue && inputBidPay <= maximumAuctionPay) {
    initialSliderValue = inputBidPay;
  }

  return (
    <PlaceBidBottomSheet
      modalState={modalState}
      workplaceName={workplaceName}
      shiftStart={startDate}
      shiftEnd={endDate}
      timezone={timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone}
      scheduledShiftDurationInHours={scheduledShiftDurationInHours}
      breakDurationInMinutes={mandatoryBreakDurationInMinutes}
      requiresBreak={Boolean(requiresLunchBreak)}
      remainingWorkDurationInHours={remainingWorkDurationInHours}
      minutesElapsed={minutesElapsed}
      auctionEndTime={auctionEndTime}
      averageWinningBid={averageWinningBidInUsd}
      averageTotalBidders={averageTotalBidders}
      minimumPay={minimumAuctionPay}
      maximumPay={maximumAuctionPay}
      isInstantPay={Boolean(isInstantPay)}
      isLoading={false}
      initialSliderValue={initialSliderValue}
      auctionBidAmount={auctionBidAmount}
      onPlaceBid={handlePlaceBid}
      onBack={handleBack}
    />
  );
}
